import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AvailabilityCalendar = ({ onDateSelect, selectedDate }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date(2025, 11, 1)); // December 2025

  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0)?.getDate();
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1)?.getDay();
  
  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];

  const availabilityData = {
    high: [5, 6, 7, 12, 13, 14, 19, 20, 21, 26, 27, 28],
    medium: [1, 2, 8, 9, 15, 16, 22, 23, 29, 30],
    low: [3, 4, 10, 11, 17, 18, 24, 25, 31],
    closed: []
  };

  const getAvailabilityStatus = (day) => {
    if (availabilityData?.closed?.includes(day)) return 'closed';
    if (availabilityData?.high?.includes(day)) return 'high';
    if (availabilityData?.medium?.includes(day)) return 'medium';
    if (availabilityData?.low?.includes(day)) return 'low';
    return 'unavailable';
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'high': return 'bg-[var(--color-success)] hover:bg-[var(--color-success)]';
      case 'medium': return 'bg-[var(--color-warning)] hover:bg-[var(--color-warning)]';
      case 'low': return 'bg-[var(--color-error)] hover:bg-[var(--color-error)]';
      case 'closed': return 'bg-[var(--color-muted)] cursor-not-allowed';
      default: return 'bg-[var(--color-muted)] cursor-not-allowed';
    }
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleDateClick = (day) => {
    const status = getAvailabilityStatus(day);
    if (status !== 'closed' && status !== 'unavailable') {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      onDateSelect(date);
    }
  };

  const isSelectedDate = (day) => {
    if (!selectedDate) return false;
    return selectedDate?.getDate() === day && 
           selectedDate?.getMonth() === currentMonth?.getMonth() &&
           selectedDate?.getFullYear() === currentMonth?.getFullYear();
  };

  return (
    <div className="bg-[var(--color-card)] rounded-lg p-6 border border-[var(--color-border)]">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-[var(--color-foreground)]">
          {monthNames?.[currentMonth?.getMonth()]} {currentMonth?.getFullYear()}
        </h3>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handlePrevMonth}>
            <Icon name="ChevronLeft" size={16} />
          </Button>
          <Button variant="outline" size="sm" onClick={handleNextMonth}>
            <Icon name="ChevronRight" size={16} />
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-2 mb-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']?.map(day => (
          <div key={day} className="text-center text-sm font-medium text-[var(--color-muted-foreground)] py-2">
            {day}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-2">
        {Array.from({ length: firstDayOfMonth })?.map((_, index) => (
          <div key={`empty-${index}`} className="aspect-square" />
        ))}
        
        {Array.from({ length: daysInMonth })?.map((_, index) => {
          const day = index + 1;
          const status = getAvailabilityStatus(day);
          const isSelected = isSelectedDate(day);
          
          return (
            <button
              key={day}
              onClick={() => handleDateClick(day)}
              disabled={status === 'closed' || status === 'unavailable'}
              className={`aspect-square rounded-lg flex items-center justify-center text-sm font-medium transition-all ${
                isSelected 
                  ? 'ring-2 ring-[var(--color-accent)] ring-offset-2 ring-offset-[var(--color-background)]' 
                  : ''
              } ${getStatusColor(status)} ${
                status !== 'closed' && status !== 'unavailable' ?'text-[var(--color-foreground)] hover:opacity-80' :'text-[var(--color-muted-foreground)] opacity-50'
              }`}
            >
              {day}
            </button>
          );
        })}
      </div>
      <div className="mt-6 grid grid-cols-2 gap-4">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-[var(--color-success)]" />
          <span className="text-sm text-[var(--color-foreground)]">High Availability</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-[var(--color-warning)]" />
          <span className="text-sm text-[var(--color-foreground)]">Medium Availability</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-[var(--color-error)]" />
          <span className="text-sm text-[var(--color-foreground)]">Low Availability</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-[var(--color-muted)]" />
          <span className="text-sm text-[var(--color-foreground)]">Unavailable</span>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityCalendar;